import * as commonConsts from '../../pages/common/CommonConsts';
import moment from 'moment';
import { getAddressLine } from './FormatHelper';

const getManualAddress = (address) => {
  if (address) {
    return {
      addressLine: address.addressLine,
      suburb: address.suburb,
      state: address.state.value,
      postCode: address.postcode
    };
  }
  return null;
};

const getDuplicateAddressDetail = (businessAddress, postalAddress) => {
  let address = [];
  if (businessAddress && businessAddress.addressNotChanged === false) {
    if (businessAddress && !businessAddress.international) {
      address = address.concat({
        addressType: 'Business',
        enterManually: businessAddress.enterManually,
        manualAddress: !businessAddress.lookupValue ? getManualAddress(businessAddress) : {},
        lookupValue: businessAddress.lookupValue ? businessAddress.lookupValue : {},
        details: businessAddress.details ? businessAddress.details : {},
        addressNotChanged: businessAddress.addressNotChanged
      });
    } else if (businessAddress && businessAddress.international) {
      address = address.concat({
        addressNotChanged: businessAddress.addressNotChanged,
        addressType: 'Business',
        details: {
          Status: 'Success',
          Address: {
            addressLine1: businessAddress.addressLine,
            suburb: businessAddress.suburb,
            state: businessAddress.state,
            postcode: businessAddress.postcode,
            country: businessAddress.country
          }
        }
      });
    }
  } else if (businessAddress) {
    address = address.concat({
      addressType: 'Business',
      addressNotChanged: businessAddress.addressNotChanged
    });
  }

  if (!postalAddress) {
    return address;
  }

  if (postalAddress.addressNotChanged === false) {
    if (postalAddress && !postalAddress.international) {
      address = address.concat({
        addressType: 'Postal',
        enterManually: postalAddress.enterManually,
        manualAddress: !postalAddress.lookupValue ? getManualAddress(postalAddress) : {},
        lookupValue: postalAddress.lookupValue ? postalAddress.lookupValue : {},
        details: postalAddress.details ? postalAddress.details : {}
      });
    } else if (postalAddress && postalAddress.international) {
      address = address.concat({
        addressNotChanged: postalAddress.addressNotChanged,
        addressType: 'Postal',
        details: {
          Status: 'Success',
          Address: {
            addressLine1: postalAddress.addressLine,
            suburb: postalAddress.suburb,
            state: postalAddress.state,
            postcode: postalAddress.postcode,
            country: postalAddress.country
          }
        }
      });
    }
  } else {
    address = address.concat({
      addressType: 'Postal',
      addressNotChanged: postalAddress.addressNotChanged
    });
  }

  return address;
};

const formattedAnnualReturnJson = (application, annualReturnDetail, authorityInformation, twoFA, duration) => {
  const { beneficiary, annualReturnInformation, trader, accountant, supportingDocuments } = annualReturnDetail;
  const { tradersSelected, newTraders, updatedPreviousTraders } = trader;
  const { selectedAuditorId, newAuditor, updatedPreviousAuditors, selectedAuditorRoleId } = accountant;
  const { beneficiaries } = beneficiary;
  const { annualReturnDocuments } = supportingDocuments;

  let associatedIndividuals = [];
  let associatedOrganisations = [];

  //#region "Individual / Organisation Traders"
  let annualReturnTraders = [];

  const getAnnualReturnPreviousTrader = (trader) => {
    const result = {
      id: trader?.id,
      firstName: trader.firstName,
      lastName: trader.lastName,
      middleName: trader.middleName,
      organisationName: trader.organisationName,
      primaryContact: trader?.id
        ? trader?.primaryContact
        : trader.primaryContact.prefix && trader.primaryContact.number
        ? `${trader.primaryContact.prefix}-${trader.primaryContact.number}`
        : '',
      email: trader?.emailAddress,
      engagementStartDate: trader?.engagementStartDate,
      engagementEndDate: trader?.engagementEndDate,
      businessAddress: trader?.businessAddress ? getAddressLine(trader?.businessAddress) : null,
      postalAddress: trader?.postalAddress ? getAddressLine(trader?.postalAddress) : null,
      type: trader?.type
    };

    return result;
  };

  const getAnnualReturnOrganisationTrader = (trader) => {
    const result = {
      id: trader?.orgDataInfo?.data?.siebelRowId,
      organisationName: trader.organisationName,
      primaryContact: trader?.id
        ? trader?.primaryContact
        : trader.organisationPrimaryContact.prefix && trader.organisationPrimaryContact.number
        ? `${trader.organisationPrimaryContact.prefix}-${trader.organisationPrimaryContact.number}`
        : '',
      email: trader?.organisationEmailAddress,
      engagementStartDate: trader?.engagementStartDate,
      engagementEndDate: trader?.engagementEndDate,
      businessAddress: trader?.organisationBusinessAddress ? getAddressLine(trader?.organisationBusinessAddress) : null,
      postalAddress: trader?.organisationPostalAddress ? getAddressLine(trader?.organisationPostalAddress) : null,
      type: trader?.type
    };

    return result;
  };

  const getUpdatedIndividualTrader = (trader) => {
    return {
      id: trader?.id,
      contactPart: {
        emailAddress: trader.emailAddress,
        phoneNo:
          trader.primaryContact.prefix && trader.primaryContact.number
            ? `${trader.primaryContact.prefix}-${trader.primaryContact.number}`
            : ''
      },
      addresses: getDuplicateAddressDetail(trader?.businessAddress, trader?.postalAddress)
    };
  };

  const getNewIndividualTrader = (trader) => {
    return {
      firstName: trader.firstName,
      surName: trader.lastName,
      middleName: trader.middleName,
      contactPart: {
        emailAddress: trader.emailAddress,
        phoneNo:
          trader.primaryContact.prefix && trader.primaryContact.number
            ? `${trader.primaryContact.prefix}-${trader.primaryContact.number}`
            : ''
      },
      addresses: getDuplicateAddressDetail(trader?.businessAddress, trader?.postalAddress),
      roleDetails: [
        {
          RoleType: 'Trader',
          StartDate: trader.startDate
        }
      ]
    };
  };

  const getNewOrganisationTrader = (trader) => {
    const result = {
      name: trader.organisationName,
      emailAddress: trader.organisationEmailAddress,
      phoneNumber:
        trader.organisationPrimaryContact.prefix && trader.organisationPrimaryContact.number
          ? `${trader.organisationPrimaryContact.prefix}-${trader.organisationPrimaryContact.number}`
          : '',
      addresses: getDuplicateAddressDetail(trader?.organisationBusinessAddress, trader?.organisationPostalAddress),
      abn: trader?.orgDataInfo?.data?.abn,
      acn: trader?.orgDataInfo?.data?.acn,
      registrationNumber:
        trader.orgDataInfo.searchCriteria.searchType === commonConsts.OrganisationSearchYincOtherTypeName
          ? trader.registrationNumber
          : null,
      roleDetails: [
        {
          RoleType: 'Trader',
          StartDate: trader.startDate
        }
      ],
      roles: []
    };

    return result;
  };

  const getNewOrganisationTraderExisits = (trader) => {
    return {
      id: trader?.orgDataInfo?.data?.siebelRowId,
      roleDetails: [
        {
          RoleType: 'Trader',
          StartDate: trader.startDate
        }
      ],
      roles: []
    };
  };

  // AR traders
  annualReturnTraders = tradersSelected.filter((x) => x.id !== 'none').map(getAnnualReturnPreviousTrader);
  annualReturnTraders = annualReturnTraders.concat(
    newTraders.filter((x) => x.type.toLowerCase() === commonConsts.OptionsIndividual).map(getAnnualReturnPreviousTrader)
  );
  annualReturnTraders = annualReturnTraders.concat(
    newTraders
      .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation)
      .map(getAnnualReturnOrganisationTrader)
  );

  // new individual traders
  associatedIndividuals = newTraders
    .filter((x) => x.type.toLowerCase() === commonConsts.OptionsIndividual)
    .map(getNewIndividualTrader);

  // updated individual traders
  associatedIndividuals = associatedIndividuals.concat(updatedPreviousTraders.map(getUpdatedIndividualTrader));

  // new organisation traders
  associatedOrganisations = newTraders
    .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation && !x.orgDataInfo?.data?.siebelRowId)
    .map(getNewOrganisationTrader);

  associatedOrganisations = associatedOrganisations.concat(
    newTraders
      .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation && x.orgDataInfo?.data?.siebelRowId)
      .map(getNewOrganisationTraderExisits)
  );

  //#endregion

  //#region "Individual Accountant"
  const getAddressForPdf = (address) => {
    if (address?.lookupValue) {
      return address?.lookupValue?.label;
    } else if (address?.enterManually) {
      let tempManualAddress = '';
      if (address?.addressLine) {
        tempManualAddress += `${address?.addressLine} `;
      }
      if (address?.suburb) {
        tempManualAddress += `${address?.suburb} `;
      }
      if (address?.state) {
        tempManualAddress += `${address?.state.text} `;
      }
      if (address?.postcode) {
        tempManualAddress += `${address?.postcode}`;
      }

      return tempManualAddress;
    } else if (address?.addressID) {
      let tempExistingAddress = '';
      if (address?.addressLine1) {
        tempExistingAddress += `${address?.addressLine1} `;
      }
      if (address?.suburb) {
        tempExistingAddress += `${address?.suburb} `;
      }
      if (address?.state) {
        tempExistingAddress += `${address?.state} `;
      }
      if (address?.postcode) {
        tempExistingAddress += `${address?.postcode}`;
      }

      return tempExistingAddress;
    } else if (address?.id) {
      let tempExistingInternationalAddress = '';
      if (address?.addressLine1) {
        tempExistingInternationalAddress += `${address?.addressLine1} `;
      }
      if (address?.suburb) {
        tempExistingInternationalAddress += `${address?.suburb} `;
      }
      if (address?.state) {
        tempExistingInternationalAddress += `${address?.state} `;
      }
      if (address?.postcode) {
        tempExistingInternationalAddress += `${address?.postcode}`;
      }

      return tempExistingInternationalAddress;
    }
  };
  const getNewIndividualAccountant = (accountant, relatedOrgName) => {
    return {
      firstName: accountant?.firstName,
      surname: accountant?.lastName,
      middleName: accountant?.middleName,
      contactPart: {
        emailAddress: accountant?.emailAddress,
        phoneNo: `${accountant?.primaryContactNumber?.prefix}-${accountant?.primaryContactNumber?.number}`
      },
      // startDate: accountant?.startDate,
      addresses: getDuplicateAddressDetail(accountant?.businessAddress, accountant?.postalAddress),
      roleDetails: [
        {
          RoleType: 'Auditor',
          StartDate: moment(new Date()).format('DD/MM/YYYY')
        }
      ],
      qualifications: [
        {
          certificateNumber: accountant?.qualification?.certificateNumber,
          certificateName: accountant?.qualification?.qualifications,
          details: accountant?.qualification?.details
        }
      ],
      relatedOrgName: relatedOrgName
    };
  };

  const getUpdatedIndividualAccountant = (accountant) => {
    return {
      id: accountant?.id,
      contactPart: {
        emailAddress: accountant?.emailAddress,
        phoneNo: accountant?.primaryContactNumber
          ? `${accountant?.primaryContactNumber.prefix}-${accountant?.primaryContactNumber?.number}`
          : accountant?.contactNo
      },
      addresses: getDuplicateAddressDetail(accountant?.businessAddress, accountant?.postalAddress),
      qualifications: [
        {
          certificateNumber: accountant?.qualification?.certificateNumber,
          certificateName: accountant?.qualification?.qualifications,
          details: accountant?.qualification?.details
        }
      ]
    };
  };

  // individual accountant
  let individualAccountantPdfData = {};
  if (accountant.selectedAuditorType.toLowerCase() === commonConsts.OptionsIndividual) {
    if (accountant?.selectedAuditorId === 'new') {
      const auditor = getNewIndividualAccountant(newAuditor);

      associatedIndividuals = [...associatedIndividuals, auditor];
      //Setup Pdf Data for new individual accountant
      individualAccountantPdfData = {
        fullName: newAuditor?.middleName
          ? `${newAuditor?.firstName} ${newAuditor?.middleName} ${newAuditor?.lastName}`
          : `${newAuditor?.firstName} ${newAuditor?.lastName}`,
        emailAddress: newAuditor?.emailAddress,
        primaryContactNumber: `${newAuditor?.primaryContactNumber?.prefix}-${newAuditor?.primaryContactNumber?.number}`,
        businessAddress: getAddressForPdf(newAuditor?.businessAddress),
        postalAddress: getAddressForPdf(newAuditor?.postalAddress),
        qualification: {
          qualificationType: newAuditor?.qualification?.qualifications,
          title:
            newAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
              ? 'Certificate number'
              : '',
          value:
            newAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
              ? newAuditor?.qualification?.certificateNumber
              : newAuditor?.qualification?.details
        }
      };
    } else if (accountant?.selectedAuditorId) {
      if (updatedPreviousAuditors) {
        const selectedPreviousIndividualAuditor = updatedPreviousAuditors.find(
          (a) => a.id === accountant?.selectedAuditorId
        );
        const auditor = getUpdatedIndividualAccountant(selectedPreviousIndividualAuditor);

        associatedIndividuals = [...associatedIndividuals, auditor];

        //Setup Pdf Data for new individual accountant
        individualAccountantPdfData = {
          fullName: selectedPreviousIndividualAuditor?.middleName
            ? `${selectedPreviousIndividualAuditor?.firstName} ${selectedPreviousIndividualAuditor?.middleName} ${selectedPreviousIndividualAuditor?.lastName}`
            : `${selectedPreviousIndividualAuditor?.firstName} ${selectedPreviousIndividualAuditor?.lastName}`,
          emailAddress: selectedPreviousIndividualAuditor?.emailAddress,
          primaryContactNumber: selectedPreviousIndividualAuditor?.primaryContactNumber
            ? `${selectedPreviousIndividualAuditor?.primaryContactNumber.prefix}-${selectedPreviousIndividualAuditor?.primaryContactNumber?.number}`
            : selectedPreviousIndividualAuditor?.contactNo,
          businessAddress: getAddressForPdf(
            selectedPreviousIndividualAuditor?.addresses.find((a) => a?.addressType === 'Business')
          ),
          postalAddress: getAddressForPdf(
            selectedPreviousIndividualAuditor?.addresses.find((a) => a?.addressType === 'Postal')
          ),
          qualification: {
            qualificationType: selectedPreviousIndividualAuditor?.qualification?.qualifications,
            title:
              selectedPreviousIndividualAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
                ? 'Certificate number'
                : '',
            value:
              selectedPreviousIndividualAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
                ? selectedPreviousIndividualAuditor?.qualification?.certificateNumber
                : selectedPreviousIndividualAuditor?.qualification?.details
          }
        };
      } else if (accountant?.selectedPreviousAuditorNotChanged) {
        //Setup Pdf Data for new individual accountant
        individualAccountantPdfData = {
          fullName: accountant?.selectedPreviousAuditorNotChanged?.middleName
            ? `${accountant?.selectedPreviousAuditorNotChanged?.firstName} ${accountant?.selectedPreviousAuditorNotChanged?.middleName} ${accountant?.selectedPreviousAuditorNotChanged?.lastName}`
            : `${accountant?.selectedPreviousAuditorNotChanged?.firstName} ${accountant?.selectedPreviousAuditorNotChanged?.lastName}`,
          emailAddress: accountant?.selectedPreviousAuditorNotChanged?.emailAddress,
          primaryContactNumber: accountant?.selectedPreviousAuditorNotChanged?.primaryContactNumber
            ? `${accountant?.selectedPreviousAuditorNotChanged?.primaryContactNumber.prefix}-${accountant?.selectedPreviousAuditorNotChanged?.primaryContactNumber?.number}`
            : accountant?.selectedPreviousAuditorNotChanged?.contactNo,
          businessAddress: getAddressForPdf(
            accountant?.selectedPreviousAuditorNotChanged?.addresses.find((a) => a?.addressType === 'Business')
          ),
          postalAddress: getAddressForPdf(
            accountant?.selectedPreviousAuditorNotChanged?.addresses.find((a) => a?.addressType === 'Postal')
          ),
          qualification: {
            qualificationType: accountant?.selectedPreviousAuditorNotChanged?.qualification?.qualifications,
            title:
              accountant?.selectedPreviousAuditorNotChanged?.qualification?.qualifications !==
              commonConsts.qualificationNoFormal
                ? 'Certificate number'
                : '',
            value:
              accountant?.selectedPreviousAuditorNotChanged?.qualification?.qualifications !==
              commonConsts.qualificationNoFormal
                ? accountant?.selectedPreviousAuditorNotChanged?.qualification?.certificateNumber
                : accountant?.selectedPreviousAuditorNotChanged?.qualification?.details
          }
        };
      }
    }
  }
  //#endregion

  //#region "Organisation Accountant"
  const getNewOrganisationAccountant = () => {
    return {
      id: newAuditor?.siebelRowId ? newAuditor?.siebelRowId : null,
      integrationId: newAuditor?.integrationId ? newAuditor?.integrationId : null,
      name: newAuditor.businessName,
      addresses: getDuplicateAddressDetail(newAuditor?.businessAddress, newAuditor?.postalAddress),
      abn: newAuditor.organisationData?.abn,
      acn: newAuditor.organisationData?.acn,
      registrationNumber:
        newAuditor.organisationSearch.searchCriteria?.searchType === commonConsts.OrganisationSearchYincOtherTypeName
          ? newAuditor?.registrationNumber
          : newAuditor?.organisationData?.yincOther,
      roleDetails: [
        {
          RoleType: 'Auditor',
          StartDate: moment(new Date()).format('DD/MM/YYYY')
        }
      ],
      roles: []
    };
  };

  let organisationAccountantPdfData = {};
  if (accountant.selectedAuditorType.toLowerCase() === commonConsts.OptionsOrganisation) {
    if (accountant?.selectedAuditorId === 'new') {
      //New accountant
      const newOrgAuditorJson = getNewOrganisationAccountant(newAuditor);
      associatedOrganisations = [...associatedOrganisations, newOrgAuditorJson];

      //New lead accountant
      const newOrgLeadAuditorJson = {
        firstName: newAuditor?.organisationDetails?.contactFirstName,
        surname: newAuditor?.organisationDetails?.contactLastName,
        middleName: newAuditor?.organisationDetails?.contactMiddleName,
        contactPart: {
          emailAddress: newAuditor?.organisationDetails?.contactEmailAddress,
          phoneNo: `${newAuditor?.organisationDetails?.contactPrimaryContactNumber.prefix}-${newAuditor?.organisationDetails?.contactPrimaryContactNumber.number}`
        },
        addresses: getDuplicateAddressDetail(
          newAuditor?.organisationDetails?.contactBusinessAddress,
          newAuditor?.organisationDetails?.contactPostalAddress
        ),
        qualifications: [
          {
            certificateNumber: newAuditor?.organisationDetails?.contactQualification.certificateNumber,
            certificateName: newAuditor?.organisationDetails?.contactQualification.qualifications,
            details: newAuditor?.organisationDetails?.contactQualification.details
          }
        ],
        roleDetails: [
          {
            RoleType: 'Auditor',
            StartDate: moment(new Date()).format('DD/MM/YYYY')
          }
        ],
        roles: [],
        relatedOrgName: newAuditor?.businessName
      };
      associatedIndividuals = [...associatedIndividuals, newOrgLeadAuditorJson];

      //Setup Pdf Data for new organisation accountant
      organisationAccountantPdfData = {
        businessName: newAuditor?.businessName,
        abn: newAuditor?.abn ? newAuditor?.abn : newAuditor?.organisationData?.abn,
        acn: newAuditor?.acn ? newAuditor?.acn : newAuditor?.organisationData?.acn,
        yincOther:
          newAuditor?.registrationNumber && newAuditor?.registrationNumber !== 'VALID'
            ? newAuditor?.registrationNumber
            : '',
        businessAddress:
          newAuditor?.businessAddress && !!!newAuditor?.businessAddress?.addressNotChanged
            ? getAddressForPdf(newAuditor?.businessAddress)
            : getAddressForPdf(newAuditor?.organisationData?.addresses.find((a) => a.addressType === 'Business')),
        postalAddress:
          newAuditor?.postalAddress && !!!newAuditor?.postalAddress?.addressNotChanged
            ? getAddressForPdf(newAuditor?.postalAddress)
            : getAddressForPdf(newAuditor?.organisationData?.addresses.find((a) => a.addressType === 'Postal')),
        leadAccountant: {
          fullName: newAuditor?.organisationDetails?.contactMiddleName
            ? `${newAuditor?.organisationDetails?.contactFirstName} ${newAuditor?.organisationDetails?.contactMiddleName} ${newAuditor?.organisationDetails?.contactLastName}`
            : `${newAuditor?.organisationDetails?.contactFirstName} ${newAuditor?.organisationDetails?.contactLastName}`,
          emailAddress: newAuditor?.organisationDetails?.contactEmailAddress,
          primaryContactNumber: `${newAuditor?.organisationDetails?.contactPrimaryContactNumber?.prefix}-${newAuditor?.organisationDetails?.contactPrimaryContactNumber?.number}`,
          businessAddress: getAddressForPdf(newAuditor?.organisationDetails?.contactBusinessAddress),
          postalAddress: getAddressForPdf(newAuditor?.organisationDetails?.contactPostalAddress),
          qualification: {
            qualificationType: newAuditor?.organisationDetails?.contactQualification?.qualifications,
            title:
              newAuditor?.organisationDetails?.contactQualification?.qualifications !==
              commonConsts.qualificationNoFormal
                ? 'Certificate number'
                : '',
            value:
              newAuditor?.organisationDetails?.contactQualification?.qualifications !==
              commonConsts.qualificationNoFormal
                ? newAuditor?.organisationDetails?.contactQualification?.certificateNumber
                : newAuditor?.organisationDetails?.contactQualification?.details
          }
        }
      };
    } else if (accountant?.selectedAuditorId && updatedPreviousAuditors !== undefined) {
      let updatedOrganisationLeadAccountantPdfData;
      //Process updated previous auditor
      const selectedPreviousOrganisationAuditor = updatedPreviousAuditors.find(
        (a) => a.id === accountant?.selectedAuditorId
      );
      const currentLeadAuditor = selectedPreviousOrganisationAuditor?.relatedIndividual;

      if (
        selectedPreviousOrganisationAuditor?.updatedData?.isLeadAccountantDetails.toLowerCase() ===
          commonConsts.organisationLeadAccountantOption2.toLowerCase() ||
        (!!!selectedPreviousOrganisationAuditor?.updatedData?.isLeadAccountantDetails &&
          selectedPreviousOrganisationAuditor?.updatedData?.newLeadAuditor?.firstName)
      ) {
        const newLeadAuditor = selectedPreviousOrganisationAuditor?.updatedData?.newLeadAuditor;

        //End Date current lead auditor
        if (currentLeadAuditor?.integrationId) {
          associatedIndividuals = associatedIndividuals.concat({
            roles: ['Auditor'],
            id: currentLeadAuditor?.id,
            integrationId: currentLeadAuditor?.integrationId,
            roleDetails: [
              {
                integrationId: currentLeadAuditor?.roleIntegrationId,
                RoleType: 'Auditor',
                EndDate: moment(new Date()).format('DD/MM/YYYY')
              }
            ]
          });
        }

        //Add new lead auditor
        const newLeadAuditorJson = {
          firstName: newLeadAuditor?.firstName,
          surname: newLeadAuditor?.lastName,
          middleName: newLeadAuditor?.middleName,
          contactPart: {
            emailAddress: newLeadAuditor?.emailAddress,
            phoneNo: `${newLeadAuditor?.primaryContactNumber.prefix}-${newLeadAuditor?.primaryContactNumber.number}`
          },
          addresses: getDuplicateAddressDetail(newLeadAuditor?.businessAddress, newLeadAuditor?.postalAddress),
          qualifications: [
            {
              certificateNumber: newLeadAuditor?.qualification.certificateNumber,
              certificateName: newLeadAuditor?.qualification.qualifications,
              details: newLeadAuditor?.qualification.details
            }
          ],
          roleDetails: [
            {
              RoleType: 'Auditor',
              StartDate: moment(new Date()).format('DD/MM/YYYY')
            }
          ],
          roles: [],
          relatedOrgName: selectedPreviousOrganisationAuditor?.businessName
        };
        associatedIndividuals = [...associatedIndividuals, newLeadAuditorJson];

        //Setup PDF data for new lead accountant
        updatedOrganisationLeadAccountantPdfData = {
          fullName: newLeadAuditor?.middleName
            ? `${newLeadAuditor?.firstName} ${newLeadAuditor?.middleName} ${newLeadAuditor?.lastName}`
            : `${newLeadAuditor?.firstName} ${newLeadAuditor?.lastName}`,
          emailAddress: newLeadAuditor?.emailAddress,
          primaryContactNumber: `${newLeadAuditor?.primaryContactNumber.prefix}-${newLeadAuditor?.primaryContactNumber.number}`,
          businessAddress: getAddressForPdf(newLeadAuditor?.businessAddress),
          postalAddress: getAddressForPdf(newLeadAuditor?.postalAddress),
          qualification: {
            qualificationType: newLeadAuditor?.qualification?.qualifications,
            title:
              newLeadAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
                ? 'Certificate number'
                : '',
            value:
              newLeadAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
                ? newLeadAuditor?.qualification?.certificateNumber
                : newLeadAuditor?.qualification?.details
          }
        };
      } else if (
        selectedPreviousOrganisationAuditor?.updatedData?.isLeadAccountantDetails.toLowerCase() ===
        commonConsts.organisationLeadAccountantOption1.toLowerCase()
      ) {
        //Update qualifications ONLY for current lead auditor
        const updatedCurrentLeadAuditor = selectedPreviousOrganisationAuditor?.updatedData?.leadAuditor;
        associatedIndividuals = associatedIndividuals.concat({
          roles: ['Auditor'],
          id: currentLeadAuditor?.id,
          integrationId: currentLeadAuditor?.integrationId,
          qualifications: [
            {
              certificateNumber: updatedCurrentLeadAuditor?.qualification.certificateNumber,
              certificateName: updatedCurrentLeadAuditor?.qualification.qualifications,
              details: updatedCurrentLeadAuditor?.qualification.details
            }
          ]
        });

        //Setup PDF data for updated lead accountant
        updatedOrganisationLeadAccountantPdfData = {
          fullName: updatedCurrentLeadAuditor?.middleName
            ? `${updatedCurrentLeadAuditor?.firstName} ${updatedCurrentLeadAuditor?.middleName} ${updatedCurrentLeadAuditor?.lastName}`
            : `${updatedCurrentLeadAuditor?.firstName} ${updatedCurrentLeadAuditor?.lastName}`,
          emailAddress: updatedCurrentLeadAuditor?.emailAddress,
          primaryContactNumber: `${updatedCurrentLeadAuditor?.primaryContactNumber.prefix}-${updatedCurrentLeadAuditor?.primaryContactNumber.number}`,
          businessAddress: getAddressForPdf(
            selectedPreviousOrganisationAuditor?.relatedIndividual?.addresses.find((a) => a.addressType === 'Business')
          ),
          postalAddress: getAddressForPdf(
            selectedPreviousOrganisationAuditor?.relatedIndividual?.addresses.find((a) => a.addressType === 'Postal')
          ),
          qualification: {
            qualificationType: updatedCurrentLeadAuditor?.qualification?.qualifications,
            title:
              updatedCurrentLeadAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
                ? 'Certificate number'
                : '',
            value:
              updatedCurrentLeadAuditor?.qualification?.qualifications !== commonConsts.qualificationNoFormal
                ? updatedCurrentLeadAuditor?.qualification?.certificateNumber
                : updatedCurrentLeadAuditor?.qualification?.details
          }
        };
      }

      organisationAccountantPdfData = {
        businessName: selectedPreviousOrganisationAuditor?.businessName,
        abn: selectedPreviousOrganisationAuditor?.abn,
        acn: selectedPreviousOrganisationAuditor?.acn,
        yincOther: selectedPreviousOrganisationAuditor?.registrationNumber,
        businessAddress: getAddressForPdf(
          selectedPreviousOrganisationAuditor?.addresses.find((a) => a.addressType === 'Business')
        ),
        postalAddress: getAddressForPdf(
          selectedPreviousOrganisationAuditor?.addresses.find((a) => a.addressType === 'Postal')
        ),
        leadAccountant: updatedOrganisationLeadAccountantPdfData
      };
    } else if (accountant?.selectedAuditorId && accountant?.selectedPreviousAuditorNotChanged) {
      //Setup Pdf Data for new organisation accountant
      organisationAccountantPdfData = {
        businessName: accountant?.selectedPreviousAuditorNotChanged?.businessName,
        abn: accountant?.selectedPreviousAuditorNotChanged?.abn,
        acn: accountant?.selectedPreviousAuditorNotChanged?.acn,
        yincOther: accountant?.selectedPreviousAuditorNotChanged?.registrationNumber,
        businessAddress: getAddressForPdf(
          accountant?.selectedPreviousAuditorNotChanged?.addresses.find((a) => a.addressType === 'Business')
        ),
        postalAddress: getAddressForPdf(
          accountant?.selectedPreviousAuditorNotChanged?.addresses.find((a) => a.addressType === 'Postal')
        ),
        leadAccountant: {
          fullName: accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.middleName
            ? `${accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.firstName} ${accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.middleName} ${accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.lastName}`
            : `${accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.firstName} ${accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.lastName}`,
          emailAddress: accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.emailAddress,
          primaryContactNumber: accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.contactNo,
          businessAddress: getAddressForPdf(
            accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.addresses.find(
              (a) => a.addressType === 'Business'
            )
          ),
          postalAddress: getAddressForPdf(
            accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.addresses.find(
              (a) => a.addressType === 'Postal'
            )
          ),
          qualification: {
            qualificationType:
              accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.qualification?.qualifications,
            title:
              accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.qualification?.qualifications !==
              commonConsts.qualificationNoFormal
                ? 'Certificate number'
                : '',
            value:
              accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.qualification?.qualifications !==
              commonConsts.qualificationNoFormal
                ? accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.qualification?.certificateNumber
                : accountant?.selectedPreviousAuditorNotChanged?.relatedIndividual?.qualification?.details
          }
        }
      };
    }
  }

  //#endregion
  const result = {
    contactId: authorityInformation.licenceInfo.partyId,
    licenceId: authorityInformation.licenceInfo.licenceRowId,
    applicationId: application.applicationId,
    applicationNo: application.applicationNo,
    licenceNumber: authorityInformation.authorityNumber,
    licenceType: 'Charitable Fundraising',
    applicantType: 'annual-return',
    authorityHolder: authorityInformation.licenceInfo.authorityHolder,
    // durationRequested: duration.durationPeriod,
    fyEndDate: annualReturnInformation.fyEndDate,
    fyStartDate: annualReturnInformation.fyStartDate,
    numberOfEmployees: annualReturnInformation.numberOfEmployees,
    fundRaisingIncome: annualReturnInformation.income,
    fundRaisingExpenditure: annualReturnInformation.expenditure,
    totalRevenue: annualReturnInformation.totalRevenue,
    totalExpense: annualReturnInformation.totalExpense,
    totalAssets: annualReturnInformation.totalAssets,
    totalLiabilities: annualReturnInformation.totalLiabilities,
    individualApplicantDetails: {
      id: 'applicant1',
      firstName: twoFA.data ? twoFA.data.firstName : '',
      middleName: twoFA.data ? twoFA.data.middleName : '',
      surname: twoFA.data ? twoFA.data.lastName : '',
      dateofbirth: null,
      placeOfBirth: null,
      twoFaVerified: twoFA.data ? true : false,
      hasOtherNames: false,
      contactPart: { emailAddress: twoFA.data.emailAddress, mobileNo: twoFA.data.mobileNumber }
    },
    beneficiaries,
    traders: annualReturnTraders,
    associatedIndividuals,
    associatedOrganisations,
    accountantId: selectedAuditorId === 'new' ? null : selectedAuditorRoleId,
    pdfData: {
      individualAccountant: individualAccountantPdfData,
      organisationAccountant: organisationAccountantPdfData
    },
    attachments: annualReturnDocuments.map((x) => {
      return {
        ...x,
        attachmentId: x.id,
        attachmentType: x.attachmentType,
        description: x.name
      };
    }),
    durationRequested: duration.durationPeriod
  };

  return result;
};

export { formattedAnnualReturnJson };
